import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { SyntheticEvent, useCallback } from 'react';
import { useMutation } from 'react-query';

import { useUserQuery } from '@hooks/query/useUserQuery';
import { useModal } from '@hooks/useModal';
import {
  FontSizes,
  Radius,
  Sizes,
  Spacing,
  Typography,
} from 'chatie-design-system';
import { ArrowRight, Icon, Pencil } from 'chatie-design-system/icon';

import Avatar, { AvatarSize } from '@components/common/Avatar';
import { DecoratedButton } from '@components/common/Button';
import EFinderImage from '@components/common/EFinderImage';
import SvgIcon from '@components/common/SvgIcon';
import FanClubModal from '@components/modal/FanClubModal';
import SubscriptionErrorModal from '@components/modal/SubscriptionErrorModal';
import Nickname from '@components/user/Nickname';

import FreeBalloon from '@public/icons/ic_free_balloon.svg';

import { fetchMySubscriptions } from '@api/balloon';
import { postToggleFollowUser } from '@api/user';
import {
  MarketItemType,
  Platform,
  QUERY_KEYS,
  SUBSCIRBE_VERSION,
  SubscriptionState,
} from '@constants';
import { useGlobalContext } from '@contexts/global';
import { IUserDetail } from '@model';
import { addDuration, formatDate, isAfter } from '@utils/date';
import {
  useMe,
  useUserMarketItemInventories,
  useUserMarketItemInventory,
} from '@utils/hook';
import {
  checkRequiredAppVersion,
  formatComma,
  openDeepLink,
  simplifyNumber,
} from '@utils/misc';
import { useOptimisticUpdate, usePaginationQuery } from '@utils/query';
import { useWebView } from '@utils/webview';

const userBackgroundCSS = css`
  z-index: 0;
`;

const userInfoWrapCSS = (hasBackground: boolean) => css`
  position: relative;
  padding: ${Spacing.medium}px;
  padding-top: ${hasBackground ? '16.6666%' : `${Spacing.xxlarge}px`};
`;

const userCSS = (theme: Theme) => css`
  position: relative;
  z-index: 2;
  text-align: center;
  > h3 {
    position: relative;
    z-index: 1;
    margin-top: ${Spacing.medium}px;
  }

  > p {
    margin-top: ${Spacing.small}px;
    font-size: ${FontSizes.small}px;
    background-color: ${theme.var.white}80;
  }
  .avatarContainer {
    display: flex;
    justify-content: center;
    position: relative;
  }
  .modifyBtn {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border-radius: ${Radius.round}px;
    border: 1px solid ${theme.var.white};
    background-color: ${theme.colors.main500};
    position: absolute;
    bottom: -3px;
    margin-left: 50px;
  }
`;

const userAvatarCSS = css``;

const userBanCSS = css`
  margin-top: ${Spacing.medium}px;

  p + h6 {
    margin-top: ${Spacing.small}px;
  }

  p {
    ${Typography.body14}
    margin-top: ${Spacing.xsmall}px;
  }
`;

const userNumericInfoCSS = (theme: Theme) => css`
  margin-left: auto;
  margin-right: auto;
  padding: 0px ${Spacing.xsmall}px;
  display: flex;
  padding-top: ${Spacing.medium}px;
  background-color: ${theme.var.white}80;

  dl {
    width: 25%;
    text-align: center;

    dt {
      font-weight: 600;
      font-size: ${FontSizes.small}px;
      margin-bottom: ${Spacing.xxsmall}px;
    }

    dd {
      font-size: ${FontSizes.xsmall}px;
      color: ${theme.var.gray600};
    }
  }

  dl + dl {
    border-left: 1px solid ${theme.var.gray100};
  }
`;

const userButtonWrapCSS = css`
  display: flex;
  margin-top: ${Spacing.medium}px;
  margin-left: -${Spacing.xxsmall}px;
  margin-right: -${Spacing.xxsmall}px;
`;

const userButtonCSS = css`
  font-size: ${FontSizes.xsmall}px;
  flex: 1 1 0px;
  height: 36px;
  border-radius: ${Radius.small}px;
  margin: 0 ${Spacing.xxsmall}px;
`;

const balloonContainerCSS = (theme: Theme) => css`
  display: flex;
  height: 48px;
  padding: ${Spacing.xsmall}px ${Spacing.small}px;
  margin: ${Spacing.xsmall}px 0px;
  border-radius: 5px;
  border: 1px solid ${theme.var.gray100};
  background: ${theme.var.gray50};

  justify-content: space-between;
  align-items: center;

  .info {
    display: flex;
    gap: ${Spacing.xxsmall}px;
    height: 14px;
    align-items: center;

    p {
      ${Typography.body12};
      line-height: 100%;
    }
  }
  .slash {
    ${Typography.body12};
    line-height: 100%;
  }
  .highlight {
    color: ${theme.colors.main500};
  }
  .weight {
    font-weight: 700;
  }
  .arrow-container {
    height: 14px;
    display: flex;
    gap: 2px;
    align-items: center;
    color: ${theme.var.gray500};
    ${Typography.body12};
  }
`;

const subscriptionCSS = (theme: Theme) => css`
  padding-top: ${Spacing.xsmall}px;
  padding-bottom: ${Spacing.xsmall}px;

  .container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: ${Spacing.xsmall}px ${Spacing.small}px;
    background: ${theme.colors.main50};
    ${Typography.body12};
    border-radius: 5px;
    border: 1px solid ${theme.var.gray100};

    div {
      display: flex;
      flex-direction: row;
      align-items: center;
    }
    svg {
      margin-right: ${Spacing.xxsmall}px;
    }
    .highlight {
      color: ${theme.colors.main500};
      font-family: 'Apple SD Gothic Neo';
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 100%;
      margin-left: 2px;
    }

    .red {
      color: ${theme.colors.red500} !important;
    }
  }
`;

interface UserInfoProps {
  userId: number;
  isMyPage?: boolean;
  balloon?: boolean;
  subscription?: boolean;
}

const UserInfo = ({
  userId,
  isMyPage,
  balloon = false,
  subscription = false,
}: UserInfoProps) => {
  const { t } = useTranslation();
  const { web } = useWebView();
  const { platform, version, goodsName, isCheming } = useGlobalContext();

  const theme = useTheme();

  const { useFetchUser } = useUserQuery();
  const { data: user } = useFetchUser(userId, {
    refetchOnWindowFocus: 'always',
  });
  const { data: me } = useMe();

  const query = usePaginationQuery(
    QUERY_KEYS.SUBSCRIPTIONS.ME,
    ({ pageParam }) => fetchMySubscriptions(pageParam),
    {
      enabled: !!subscription,
      refetchOnWindowFocus: 'always',
    }
  );

  const [
    isSubsErrorModalOpen,
    { open: openSubsErrorModal, close: closeSubsErrorModal },
  ] = useModal('subs-error-modal');

  const subscriptionState = query?.data?.pages?.[0]?.items?.[0]?.status;
  const subscriptionItem = query?.data?.pages?.[0]?.items?.[0];

  const current = new Date();

  const onHourAfterSubscribedTime = subscriptionItem?.subscribedAt
    ? addDuration(subscriptionItem?.subscribedAt, {
        hours: 1,
      })
    : undefined;
  const isAfterOneHourFromSubscription = onHourAfterSubscribedTime
    ? isAfter(current, onHourAfterSubscribedTime)
    : false;

  const isInProgress =
    subscriptionState === SubscriptionState.inProgress ||
    subscriptionState === SubscriptionState.inGracePeriod;
  const isExpired =
    subscriptionState === SubscriptionState.expired ||
    subscriptionState === SubscriptionState.canceled;
  const isInHold = subscriptionState === SubscriptionState.onHold;
  const isPending =
    subscriptionState === SubscriptionState.pending &&
    isAfterOneHourFromSubscription;

  useUserMarketItemInventories([user]);
  const { data, getDecoration } = useUserMarketItemInventory(user?.id);

  const optimistic = useOptimisticUpdate();
  const { mutate: toggleFollow } = useMutation(postToggleFollowUser);
  const router = useRouter();
  const isOverVersion = checkRequiredAppVersion(
    platform,
    version,
    SUBSCIRBE_VERSION
  );

  const [
    isFanClubModalOpen,
    { open: openFanClubModal, close: closeFanClubModal },
  ] = useModal('fan-club-modal');

  const handleModifyProfile = () => {
    if (isMyPage && platform !== Platform.web) {
      web.openPage('/me/profile');
    } else {
      router.push('/me/profile');
    }
  };

  const handleGoToFanPage = () => {
    if (!user?.isFan && me?.id !== user?.id) {
      openFanClubModal();
      return;
    }
    if (isMyPage) {
      if (platform == Platform.web) {
        router.push('/me/fan-club');
      } else {
        web.openPage('/me/fan-club');
      }
    } else {
      if (platform == Platform.web) {
        router.push(`/users/${userId}/fan-club`);
      } else {
        web.openPage(`/users/${userId}/fan-club`);
      }
    }
  };

  const handleGoToFollowerPage = () => {
    if (!user?.isFan && me?.id !== user?.id) {
      openFanClubModal();
      return;
    }

    if (isMyPage && platform !== Platform.web) {
      web.openPage(`/users/${userId}/followers`);
    } else {
      router.push(`/users/${userId}/followers`);
    }
  };

  const handleGoToFollowingPage = () => {
    if (isMyPage && platform !== Platform.web) {
      web.openPage(`/users/${userId}/followings`);
    } else {
      router.push(`/users/${userId}/followings`);
    }
  };

  const handleToggleFollow = useCallback(() => {
    if (!user) {
      return;
    }

    toggleFollow(user.id, {
      onSuccess: () => {
        optimistic.update<IUserDetail>(QUERY_KEYS.USER.ONE(userId), (user) => ({
          ...user,
          isFollowing: !user.isFollowing,
        }));
      },
    });
  }, [user?.id]);

  const handleClickPayment = (e: SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (platform === Platform.web) {
      return router.push('/payment');
    }
    if (
      checkRequiredAppVersion(platform, version, {
        iOS: '2.26.1',
        android: '2.32.4',
      })
    ) {
      return web.openPage('/payment');
    } else {
      return openDeepLink('chatie-app://payment', router);
    }
  };
  const handleClickSubscription = () => {
    if (isPending) {
      openSubsErrorModal();
      return;
    }
    if (platform === Platform.web) {
      return router.push('/me/subscriptions');
    }
    web.openPage('/me/subscriptions');
  };

  if (!user) {
    return null;
  }

  const backgroundDecoration = getDecoration(data, MarketItemType.profileBg)
    ?.object.pageProfile;

  const profileBtnDecoration = getDecoration(data, MarketItemType.profileBtn);

  return (
    <>
      <main css={userInfoWrapCSS(!!backgroundDecoration)}>
        {backgroundDecoration && (
          <section css={userBackgroundCSS}>
            <EFinderImage
              image={backgroundDecoration}
              objectPosition="center top"
            />
          </section>
        )}
        <div css={userCSS}>
          <div className="avatarContainer">
            <Avatar css={userAvatarCSS} size={AvatarSize.xlarge} {...user} />
            {userId === me?.id && (
              <div className="modifyBtn" onClick={handleModifyProfile}>
                <Icon
                  icon={Pencil}
                  size={Sizes.xsmall}
                  color={theme.var.white}
                />
              </div>
            )}
          </div>
          <h3>
            <Nickname {...user} size={Sizes.small} />
          </h3>
          {user.ban ? (
            <div css={userBanCSS}>
              <h6>{t('정지 기간')}</h6>
              <p>
                {isAfter(user.ban.ended, '2070-01-01T00:00:00')
                  ? t('영구정지')
                  : `${formatDate(user.ban.started)} ~ ${formatDate(
                      user.ban.ended
                    )}`}
              </p>
              <h6>{t('정지 사유')}</h6>
              <p>{user.ban.reason}</p>
            </div>
          ) : user.introduce ? (
            <p className="multiline">{user.introduce}</p>
          ) : null}
          {balloon && (
            <div css={balloonContainerCSS}>
              <div className="info">
                {!isCheming && <SvgIcon icon={FreeBalloon} />}
                <p>
                  {t(`보유 ${goodsName} `)}
                  <span className="highlight weight">
                    {t(`${formatComma(me?.balloon ?? 0)}`)}
                  </span>
                  <span className="highlight">{t(`개`)}</span>
                </p>
                {isMyPage && (
                  <>
                    <span className="slash">{t('/')}</span>
                    <p>
                      {t('대화권 ')}
                      <span className="highlight weight">
                        {t(`${formatComma(me?.chatToken?.tokenCount ?? 0)}`)}
                      </span>
                      <span className="highlight">{t(`개`)}</span>
                    </p>
                  </>
                )}
              </div>
              <div className="arrow-container" onClick={handleClickPayment}>
                {t('충전하기')}
                <Icon
                  icon={ArrowRight}
                  size={Sizes.xsmall}
                  color={theme.var.gray500}
                />
              </div>
            </div>
          )}
          {subscription &&
            isOverVersion &&
            !!subscriptionState &&
            !isCheming && (
              <div css={subscriptionCSS}>
                <div className="container" onClick={handleClickSubscription}>
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M8 7.83334V8.16668M8 5.33334V5.66668M8 10.3333V10.6667M12.6667 12.6667C13.0203 12.6667 13.3594 12.5262 13.6095 12.2762C13.8595 12.0261 14 11.687 14 11.3333V9.33334C13.6464 9.33334 13.3072 9.19287 13.0572 8.94282C12.8071 8.69277 12.6667 8.35363 12.6667 8.00001C12.6667 7.64639 12.8071 7.30725 13.0572 7.0572C13.3072 6.80715 13.6464 6.66668 14 6.66668V4.66668C14 4.31305 13.8595 3.97392 13.6095 3.72387C13.3594 3.47382 13.0203 3.33334 12.6667 3.33334H3.33333C2.97971 3.33334 2.64057 3.47382 2.39052 3.72387C2.14048 3.97392 2 4.31305 2 4.66668V6.66668C2.35362 6.66668 2.69276 6.80715 2.94281 7.0572C3.19286 7.30725 3.33333 7.64639 3.33333 8.00001C3.33333 8.35363 3.19286 8.69277 2.94281 8.94282C2.69276 9.19287 2.35362 9.33334 2 9.33334V11.3333C2 11.687 2.14048 12.0261 2.39052 12.2762C2.64057 12.5262 2.97971 12.6667 3.33333 12.6667H12.6667Z"
                        stroke="currentColor"
                        stroke-width="1.25"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>

                    {t(
                      isExpired
                        ? '지금 다시 구독하고 할인을 놓치지 마세요!'
                        : isPending
                        ? '문제가 생겼어요. 여기를 클릭해 내용을 확인해 주세요.'
                        : '나의 구독'
                    )}
                    <span className={`highlight ${isInHold ? 'red' : ''}`}>
                      {t(
                        isInProgress
                          ? '플레이 박스 정기 구독중'
                          : isInHold
                          ? '결제 실패 상태에요'
                          : ''
                      )}
                    </span>
                  </div>
                  <div className="arrow-container">
                    <Icon
                      icon={ArrowRight}
                      size={Sizes.xsmall}
                      color={theme.var.gray500}
                    />
                  </div>
                </div>
              </div>
            )}
          {!isCheming && (
            <div css={userNumericInfoCSS}>
              <dl>
                <dt>{simplifyNumber({ value: user.storyTapCount })}</dt>
                <dd>{t('탭')}</dd>
              </dl>

              <dl onClick={handleGoToFanPage}>
                <dt>{simplifyNumber({ value: user.fanCount })}</dt>
                <dd>{t('팬')}</dd>
              </dl>

              <dl onClick={handleGoToFollowerPage}>
                <dt>{simplifyNumber({ value: user.followerCount })}</dt>
                <dd>{t('팔로워')}</dd>
              </dl>

              <dl onClick={handleGoToFollowingPage}>
                <dt>{simplifyNumber({ value: user.followingCount })}</dt>
                <dd>{t('팔로잉')}</dd>
              </dl>
            </div>
          )}
          {userId !== me?.id && !isCheming && (
            <div css={userButtonWrapCSS}>
              <DecoratedButton
                css={userButtonCSS}
                decoration={profileBtnDecoration}
                onClick={handleToggleFollow}
              >
                {user.isFollowing ? t('팔로우 중') : t('팔로우')}
              </DecoratedButton>
            </div>
          )}
        </div>
        <FanClubModal open={isFanClubModalOpen} onClose={closeFanClubModal} />
        <SubscriptionErrorModal
          open={isSubsErrorModalOpen}
          onClose={closeSubsErrorModal}
          name={'subs-error-modal'}
        />
      </main>
    </>
  );
};

export default UserInfo;
